import { dataLayerPush } from '@nrdy-marketing-engine/gtm/src/data-layer-actions';
import { useUserContext } from '@nrdy-marketing-engine/server/src/user/context';
import { useCallback } from 'react';
import { Client } from '../../../@types/cart-state';
import { trackCartEvent, trackCartFailure } from '../../lib/track-service/track-cart';

export default function useOnCreateAccount({ setLoading, onContinue, setError, onSwitchForm }) {
  const { createUserAndStore, signInAndStore } = useUserContext();

  const onSuccess = useCallback(
    async (result: Client, data) => {
      if (!data) throw new Error('Unexpected error, could not create user');

      const payload = {
        email: result.email,
        has_membership: result.hasMembership,
        name: `${result.firstName} ${result.lastName}`,
      };
      await trackCartEvent(
        'checkout_create_account_success',
        {
          clientUuid: result.uuid,
        },
        payload
      );

      dataLayerPush('create_account', payload);

      onContinue({ client: result });
    },
    [onContinue]
  );

  const onFailure = useCallback(
    async ({ message }, client: Client) => {
      setError('email', { message });

      if (message.includes('already associated')) {
        await onSwitchForm();
        try {
          await signInAndStore({ email: client.email, password: client.password });
          onContinue({ client });
        } catch {
          setLoading(false);
        }
      }

      await trackCartFailure(
        'checkout_create_account_failure',
        {
          clientUuid: null,
          error: message,
        },
        {
          client,
        }
      );
    },
    [setError, setLoading, onSwitchForm, onContinue, signInAndStore]
  );

  const createUser = useCallback(
    (client: Client) => {
      setLoading(true);

      return createUserAndStore(client)
        .then((data) => onSuccess(client, data))
        .catch((e) => onFailure(e, client))
        .finally(() => setLoading(false));
    },
    [setLoading, onSuccess, onFailure, createUserAndStore]
  );

  return createUser;
}
